/**
 * Typisierter API-Client.
 *
 * Jede Anfrage traegt ein frisch bezogenes Zugriffstoken sowie eine
 * Korrelations-Id, die serverseitig in Logs und Audit-Eintraegen erscheint.
 * Die Berechtigungspruefung erfolgt ausschliesslich serverseitig; die im
 * Frontend bekannten Berechtigungen dienen nur der Darstellung.
 */
import type { IPublicClientApplication, AccountInfo } from '@azure/msal-browser';
import { BrowserAuthError, InteractionRequiredAuthError } from '@azure/msal-browser';
import { getAuthConfig } from './authConfig.js';

/** Leer = gleiche Herkunft (verknuepftes Backend der Static Web App unter /api). */
const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/$/, '');

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly correlationId?: string;
  /**
   * true, wenn die Korrelations-Id aus der Antwort des Servers stammt.
   *
   * Der Unterschied ist fuer die Fehlersuche wesentlich: Nur eine vom Server
   * gemeldete Id steht auch in seinen Logs. Antwortet eine Zwischenschicht
   * (Static Web Apps, der Functions-Host bei einem Routenkonflikt), gibt es
   * keinen Logeintrag – die Suche nach der angezeigten Nummer bleibt dann
   * ergebnislos und fuehrt in die Irre.
   */
  readonly correlationIdFromServer: boolean;
  readonly details?: unknown;

  constructor(
    status: number,
    code: string,
    message: string,
    correlationId?: string,
    details?: unknown,
    correlationIdFromServer = false,
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.correlationId = correlationId;
    this.correlationIdFromServer = correlationIdFromServer;
    this.details = details;
  }

  /** true, wenn eine erneute Anmeldung erforderlich ist. */
  get requiresSignIn(): boolean {
    return this.status === 401;
  }
}

export interface ApiClientOptions {
  instance: IPublicClientApplication;
  account: AccountInfo;
  /** Wird aufgerufen, wenn die Sitzung endgueltig ungueltig ist. */
  onSessionExpired?: () => void;
}

function newCorrelationId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  return `c-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Erkennt eine gescheiterte stille Token-Erneuerung im versteckten iframe.
 *
 * MSAL meldet das als BrowserAuthError und NICHT als
 * InteractionRequiredAuthError – obwohl genau das die Loesung ist. Ohne diese
 * Unterscheidung landet der Fall als roter Fehlerbildschirm bei Personen, die
 * lediglich eine neue Anmeldung braeuchten.
 *
 * Haeufigste Ursachen: Der Browser blockiert Cookies von Drittanbietern, oder
 * das Zeitfenster von sechs Sekunden reichte nicht.
 */
export function isSilentRenewalFailure(error: unknown): boolean {
  if (!(error instanceof BrowserAuthError)) return false;
  return ['monitor_window_timeout', 'iframe_closed_prematurely', 'silent_prompt_value_error'].includes(error.errorCode);
}

export class ApiClient {
  constructor(private readonly options: ApiClientOptions) {}

  private async getAccessToken(): Promise<string> {
    try {
      const result = await this.options.instance.acquireTokenSilent({
        ...getAuthConfig().silentRequest,
        account: this.options.account,
      });
      return result.accessToken;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError || isSilentRenewalFailure(error)) {
        // Benutzerinteraktion noetig: abgelaufene Sitzung, MFA-Anforderung –
        // oder die stille Erneuerung im iframe kam nicht durch. Letzteres ist
        // kein Anwendungsfehler, sondern der Normalfall, sobald der Browser
        // Cookies von Drittanbietern blockiert: Das iframe erreicht
        // login.microsoftonline.com nicht mit der bestehenden Sitzung. Beide
        // Faelle loesen dasselbe – eine sichtbare Anmeldung.
        await this.options.instance.acquireTokenRedirect({
          ...getAuthConfig().silentRequest,
          // Die sichtbare Anmeldung gehoert in die Anwendung zurueck, nicht auf
          // die leere Erneuerungsseite.
          redirectUri: window.location.origin,
          account: this.options.account,
        });
        throw new ApiError(401, 'unauthorized', 'Erneute Anmeldung erforderlich.');
      }
      throw error;
    }
  }

  private async request(path: string, init: RequestInit = {}): Promise<Response> {
    const token = await this.getAccessToken();
    const correlationId = newCorrelationId();

    let response: Response;
    try {
      response = await fetch(`${API_BASE_URL}${path}`, {
        ...init,
        headers: {
          Authorization: `Bearer ${token}`,
          'x-correlation-id': correlationId,
          ...(init.body ? { 'Content-Type': 'application/json' } : {}),
          ...init.headers,
        },
      });
    } catch {
      throw new ApiError(0, 'network_error', 'Der Server ist nicht erreichbar.', correlationId);
    }

    if (response.ok) return response;

    let code = 'internal_error';
    let message = 'Es ist ein unerwarteter Fehler aufgetreten.';
    let details: unknown;
    const headerCorrelationId = response.headers.get('x-correlation-id');
    let serverCorrelationId = headerCorrelationId ?? undefined;
    try {
      const body = (await response.json()) as { code?: string; message?: string; correlationId?: string; details?: unknown };
      code = body.code ?? code;
      message = body.message ?? message;
      details = body.details;
      serverCorrelationId = body.correlationId ?? serverCorrelationId;
    } catch {
      // Antwort ohne JSON-Koerper – Standardmeldung beibehalten. Das ist der
      // Fall, wenn nicht die Anwendung antwortet, sondern eine Schicht davor.
    }

    if (response.status === 401) this.options.onSessionExpired?.();
    throw new ApiError(
      response.status,
      code,
      message,
      serverCorrelationId ?? correlationId,
      details,
      serverCorrelationId !== undefined,
    );
  }

  async get<T>(path: string, signal?: AbortSignal): Promise<T> {
    const response = await this.request(path, { method: 'GET', signal });
    return (await response.json()) as T;
  }

  async post<T>(path: string, body?: unknown, signal?: AbortSignal): Promise<T> {
    const response = await this.request(path, {
      method: 'POST',
      body: body === undefined ? undefined : JSON.stringify(body),
      signal,
    });
    if (response.status === 204) return undefined as T;
    return (await response.json()) as T;
  }

  async put<T>(path: string, body: unknown, signal?: AbortSignal): Promise<T> {
    const response = await this.request(path, { method: 'PUT', body: JSON.stringify(body), signal });
    return (await response.json()) as T;
  }

  async patch<T>(path: string, body: unknown, signal?: AbortSignal): Promise<T> {
    const response = await this.request(path, { method: 'PATCH', body: JSON.stringify(body), signal });
    return (await response.json()) as T;
  }

  async delete(path: string, signal?: AbortSignal): Promise<void> {
    await this.request(path, { method: 'DELETE', signal });
  }

  /**
   * Loeschen mit Antwortkoerper. Einige Endpunkte liefern den neu berechneten
   * Datensatz zurueck – etwa das Produkt nach dem Entfernen einer
   * Kostenposition. Das erspart einen zweiten Aufruf und damit ein Zeitfenster,
   * in dem die Anzeige nicht zum Datenbestand passt.
   */
  async deleteJson<T>(path: string, signal?: AbortSignal): Promise<T> {
    const response = await this.request(path, { method: 'DELETE', signal });
    return (await response.json()) as T;
  }

  /**
   * Laedt Binaerdaten hoch (Katalogbilder). Bewusst kein multipart/form-data:
   * Der Koerper ist genau eine Datei, und ein roher Koerper spart das Parsen
   * eines Mehrteil-Formats auf der Serverseite – weniger Code, der Eingaben
   * auseinandernimmt, ist hier ein Sicherheitsgewinn.
   */
  async postBinary<T>(path: string, data: Blob, signal?: AbortSignal): Promise<T> {
    const response = await this.request(path, {
      method: 'POST',
      body: data,
      headers: { 'Content-Type': data.type || 'application/octet-stream' },
      signal,
    });
    return (await response.json()) as T;
  }

  /**
   * Holt ein Bild und liefert eine Objekt-URL dafuer.
   *
   * Ein schlichtes <img src="/api/…"> funktioniert hier nicht: Der Endpunkt
   * verlangt ein Bearer-Token, das der Browser bei einer Bildanfrage nicht
   * mitschickt. Die aufrufende Stelle muss die URL nach Gebrauch wieder
   * freigeben (URL.revokeObjectURL).
   */
  async objectUrl(path: string, signal?: AbortSignal): Promise<string> {
    const response = await this.request(path, { method: 'GET', signal });
    return URL.createObjectURL(await response.blob());
  }

  /**
   * Laedt eine Datei herunter. Der Blob-Umweg ist noetig, weil der Endpunkt ein
   * Bearer-Token erwartet und daher nicht als einfacher Link aufgerufen werden kann.
   */
  async download(path: string, fallbackFileName: string): Promise<void> {
    const response = await this.request(path, { method: 'GET' });
    const blob = await response.blob();
    const disposition = response.headers.get('content-disposition') ?? '';
    const match = /filename="?([^"';]+)"?/i.exec(disposition);
    const fileName = match?.[1] ?? fallbackFileName;

    const url = URL.createObjectURL(blob);
    try {
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = fileName;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
    } finally {
      // Freigabe verzoegern, damit der Download sicher gestartet ist.
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
    }
  }
}
